import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WantedPoster from "../components/WantedPoster";
import AudioToggle from "../components/AudioToggle";
import { DIFFICULTIES, TEAMS } from "../lib/game";
import { usePlayer } from "../context/PlayerContext";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, LogOut, Check, Trophy } from "lucide-react";
import { playTick } from "../lib/audio";

const TeamCard = ({ id, label, tagline, color, image, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => { onSelect(id); playTick(); }}
    data-testid={`team-${id}-card`}
    className="relative bg-white border-4 border-[color:var(--deepest-brown)] p-4 sm:p-5 flex items-center gap-4 transition-all"
    style={{
      boxShadow: selected ? `8px 8px 0 0 var(--red)` : "5px 5px 0 0 rgba(58,29,4,0.45)",
      transform: selected ? "translate(-2px, -2px)" : "none",
    }}
  >
    <img
      src={image}
      alt={label}
      style={{ height: 130, width: "auto", filter: "drop-shadow(4px 5px 0 rgba(58,29,4,0.35))" }}
      draggable={false}
    />
    <div className="text-left flex-1">
      <div className="font-anton text-2xl text-[color:var(--deepest-brown)]" style={{ color }}>
        {label.toUpperCase()}
      </div>
      <div className="font-elite text-sm text-[color:var(--dark-brown)] mt-1">{tagline}</div>
    </div>
    {selected && (
      <div
        className="absolute -top-3 -right-3 w-9 h-9 bg-[color:var(--red)] border-3 border-[color:var(--deepest-brown)] flex items-center justify-center"
        style={{ borderWidth: 3, borderStyle: "solid" }}
      >
        <Check size={18} color="#fff" strokeWidth={3} />
      </div>
    )}
  </button>
);

const Lobby = () => {
  const navigate = useNavigate();
  const { username, team, setTeam, clear } = usePlayer();
  const [selected, setSelected] = useState("bandit");
  const [chosenTeam, setChosenTeam] = useState(team || "");
  // Mobile auto-start should only fire AFTER an explicit opponent click —
  // not from the default `bandit` pre-selection or a previously-saved team.
  const [foeClicked, setFoeClicked] = useState(false);

  // On mobile, auto-start the duel once both team is set AND the user has
  // actively clicked an opponent (foeClicked === true).
  const isMobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
  useEffect(() => {
    if (!username) return;
    if (!isMobile) return;
    if (!foeClicked || !chosenTeam || !selected) return;
    const t = setTimeout(() => {
      setTeam(chosenTeam);
      navigate(`/duel/${selected}`);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foeClicked, chosenTeam, selected, isMobile, username]);

  if (!username) {
    navigate("/");
    return null;
  }

  const start = () => {
    if (!chosenTeam) return toast.error("Pick your partner first, partner.");
    if (!selected) return toast.error("Pick your foe, stranger.");
    playTick();
    setTeam(chosenTeam);
    navigate(`/duel/${selected}`);
  };

  const rotates = { rookie: -2, bandit: 1, sheriff: -1 };

  return (
    <div className="min-h-screen bg-yellow-brand">
      {/* top bar */}
      <div className="px-5 sm:px-12 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          data-testid="lobby-back-button"
          className="font-elite text-sm flex items-center gap-2 text-[color:var(--deepest-brown)] hover:text-[color:var(--red)]"
        >
          <ArrowLeft size={16} /> back to title
        </button>
        <div className="flex items-center gap-3 font-elite text-sm">
          <span className="text-[color:var(--deepest-brown)]">
            slurpin' as <strong className="font-anton text-base">{username}</strong>
          </span>
          <AudioToggle ambient />
          <button
            onClick={() => navigate("/leaderboard")}
            data-testid="lobby-leaderboard-button"
            className="flex items-center gap-1 underline decoration-dotted text-[color:var(--deepest-brown)]"
          >
            <Trophy size={14} /> leaderboard
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="lobby-dashboard-button"
            className="flex items-center gap-1 underline decoration-dotted text-[color:var(--deepest-brown)]"
          >
            <BarChart3 size={14} /> ledger
          </button>
          <button
            onClick={() => { clear(); navigate("/"); }}
            data-testid="lobby-logout-button"
            className="flex items-center gap-1 text-[color:var(--red)]"
          >
            <LogOut size={14} /> sign out
          </button>
        </div>
      </div>

      <div className="text-center max-w-3xl mx-auto px-5 mt-2 mb-8">
        <div className="font-elite text-xs tracking-[0.3em] text-[color:var(--deepest-brown)]">
          — THE SALOON —
        </div>
        <h2 className="font-display text-4xl sm:text-6xl mt-2" style={{ color: "var(--red)", textShadow: "2px 2px 0 var(--yellow), 4px 4px 0 var(--deepest-brown)" }}>
          PICK YOUR PARTNER
        </h2>
        <p className="font-elite text-sm sm:text-base mt-3 text-[color:var(--deepest-brown)]">
          Think you're the fastest slurp in the west? Put it to the test in the Smokehouse
          Showdown. Best of 5 rounds, first to 3 slurps wins the duel. The loser gets smoked.
        </p>
      </div>

      {/* Pick Your Partner */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto px-5">
        {Object.entries(TEAMS).map(([id, t]) => (
          <TeamCard
            key={id}
            id={id}
            label={t.label}
            tagline={t.tagline}
            color={t.color}
            image={t.image}
            selected={chosenTeam === id}
            onSelect={setChosenTeam}
          />
        ))}
      </div>

      {/* Pick Your Foe */}
      <div className="text-center max-w-3xl mx-auto px-5 mt-12 mb-6">
        <div className="font-elite text-xs tracking-[0.3em] text-[color:var(--deepest-brown)]">
          — THEN PICK YOUR FOE —
        </div>
        <h2 className="font-display text-3xl sm:text-5xl mt-2" style={{ color: "var(--red)", textShadow: "2px 2px 0 var(--yellow), 3px 3px 0 var(--deepest-brown)" }}>
          WHO YA SLINGIN' AT?
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto px-5">
        {Object.entries(DIFFICULTIES).map(([id, cfg]) => (
          <WantedPoster
            key={id}
            id={id}
            label={cfg.label}
            tagline={cfg.tagline}
            bounty={cfg.bountyDollars}
            color={cfg.color}
            selected={selected === id}
            onSelect={(s) => { setSelected(s); setFoeClicked(true); playTick(); }}
            rotate={rotates[id]}
          />
        ))}
      </div>

      <div className="mt-10 mb-12 flex justify-center">
        <button data-testid="start-duel-button" onClick={start} className="btn-logo text-xl">
          <span>Start Showdown</span>
        </button>
      </div>
    </div>
  );
};

export default Lobby;
